/**
 * MobileRecoveryTab.jsx
 * Recovery & sleep tracking for the mobile NutritionScreen
 * Shows recovery status, sleep quality, and muscle soreness
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Moon, Loader2, Activity, Heart, Zap, ThermometerSun, Droplet } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MUSCLE_GROUPS = [
  { id: 'chest', icon: '💪' },
  { id: 'back', icon: '🔙' },
  { id: 'shoulders', icon: '🏋️' },
  { id: 'arms', icon: '💪' },
  { id: 'legs', icon: '🦵' },
  { id: 'core', icon: '🎯' },
];

const RECOVERY_LEVELS = [
  { value: 1, color: 'bg-red-500' },
  { value: 2, color: 'bg-orange-500' },
  { value: 3, color: 'bg-yellow-500' },
  { value: 4, color: 'bg-green-400' },
  { value: 5, color: 'bg-green-500' },
];

const MobileRecoveryTab = ({ portalToken, selectedDate }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [recoveryData, setRecoveryData] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [sleepHours, setSleepHours] = useState('7.5');
  const [sleepQuality, setSleepQuality] = useState(3);
  const [recoveryScore, setRecoveryScore] = useState(3);
  const [soreness, setSoreness] = useState({});
  const [stressLevel, setStressLevel] = useState(3);
  const [hydration, setHydration] = useState(3);

  useEffect(() => {
    loadRecoveryData();
  }, [portalToken, selectedDate]);

  const loadRecoveryData = async () => {
    if (!portalToken) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/portal/nutrition/recovery`, {
        params: { date: selectedDate },
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      const data = response.data;
      if (data) {
        setRecoveryData(data);
        if (data.sleep_hours) setSleepHours(String(data.sleep_hours));
        if (data.sleep_quality) setSleepQuality(data.sleep_quality);
        if (data.recovery_score) setRecoveryScore(data.recovery_score);
        if (data.soreness) setSoreness(data.soreness);
        if (data.stress_level) setStressLevel(data.stress_level);
        if (data.hydration) setHydration(data.hydration);
      }
    } catch (error) {
      console.error('Error loading recovery data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!portalToken) return;
    setSaving(true);
    try {
      await axios.post(`${API}/portal/nutrition/recovery`, {
        date: selectedDate,
        sleep_hours: parseFloat(sleepHours) || 0,
        sleep_quality: sleepQuality,
        recovery_score: recoveryScore,
        soreness,
        stress_level: stressLevel,
        hydration
      }, { headers: { Authorization: `Bearer ${portalToken}` } });
      setRecoveryData({ sleep_hours: sleepHours, sleep_quality: sleepQuality, recovery_score: recoveryScore });
    } catch (error) {
      // Recovery endpoint might not exist - that's okay
      console.error('Recovery save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const toggleSoreness = (muscleId) => {
    setSoreness(prev => {
      const current = prev[muscleId] || 0;
      const next = current >= 3 ? 0 : current + 1;
      return { ...prev, [muscleId]: next };
    });
  };

  const getSorenessColor = (level) => {
    if (!level || level === 0) return 'bg-zinc-700 border-zinc-600';
    if (level === 1) return 'bg-yellow-500/20 border-yellow-500/50';
    if (level === 2) return 'bg-orange-500/20 border-orange-500/50';
    return 'bg-red-500/20 border-red-500/50';
  };

  const getSorenessLabel = (level) => {
    if (!level || level === 0) return t('recovery.soreness_ok');
    if (level === 1) return t('recovery.soreness_light');
    if (level === 2) return t('recovery.soreness_medium');
    return t('recovery.soreness_strong');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 pb-32" data-testid="mobile-recovery-tab">
      {/* Recovery Score */}
      <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 rounded-2xl p-4 border border-purple-500/30">
        <div className="flex items-center gap-2 mb-3">
          <Heart className="w-5 h-5 text-purple-400" />
          <span className="text-white font-semibold">{t('recovery.score')}</span>
        </div>
        <div className="flex gap-2">
          {RECOVERY_LEVELS.map(level => (
            <button
              key={level.value}
              onClick={() => setRecoveryScore(level.value)}
              className={`flex-1 py-2.5 rounded-xl text-center transition-all ${
                recoveryScore === level.value
                  ? `${level.color} text-white font-medium shadow-lg`
                  : 'bg-zinc-800 text-zinc-500'
              }`}
              data-testid={`recovery-score-${level.value}`}
            >
              <span className="text-xs">{level.value}</span>
            </button>
          ))}
        </div>
        <p className="text-zinc-400 text-xs mt-2 text-center">
          {t(`recovery.levels.${recoveryScore}`)}
        </p>
      </div>

      {/* Sleep */}
      <div className="bg-zinc-800/50 rounded-2xl p-4 border border-zinc-700/50">
        <div className="flex items-center gap-2 mb-3">
          <Moon className="w-4 h-4 text-blue-400" />
          <span className="text-zinc-300 text-sm font-medium">{t('recovery.sleep')}</span>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-zinc-500 text-xs mb-1 block">{t('recovery.sleep_hours')}</label>
            <input
              type="number"
              value={sleepHours}
              onChange={(e) => setSleepHours(e.target.value)}
              step="0.5"
              min="0"
              max="16"
              className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-3 py-2 text-white text-sm"
              data-testid="sleep-hours-input"
            />
          </div>
          <div>
            <label className="text-zinc-500 text-xs mb-1 block">{t('recovery.sleep_quality')}</label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map(q => (
                <button
                  key={q}
                  onClick={() => setSleepQuality(q)}
                  className={`flex-1 py-2 rounded-lg text-xs transition-colors ${
                    sleepQuality >= q
                      ? 'bg-blue-500/30 text-blue-400 border border-blue-500/50'
                      : 'bg-zinc-700 text-zinc-500 border border-zinc-600'
                  }`}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Stress & Hydration */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-zinc-800/50 rounded-2xl p-3 border border-zinc-700/50">
          <div className="flex items-center gap-1.5 mb-2">
            <ThermometerSun className="w-3.5 h-3.5 text-red-400" />
            <span className="text-zinc-400 text-xs">{t('recovery.stress')}</span>
          </div>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map(s => (
              <button
                key={s}
                onClick={() => setStressLevel(s)}
                className={`flex-1 py-1.5 rounded-lg text-[10px] transition-colors ${
                  stressLevel >= s
                    ? 'bg-red-500/20 text-red-400 border border-red-500/40'
                    : 'bg-zinc-700 text-zinc-500 border border-zinc-600'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-zinc-800/50 rounded-2xl p-3 border border-zinc-700/50">
          <div className="flex items-center gap-1.5 mb-2">
            <Droplet className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-zinc-400 text-xs">{t('recovery.hydration')}</span>
          </div>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map(h => (
              <button
                key={h}
                onClick={() => setHydration(h)}
                className={`flex-1 py-1.5 rounded-lg text-[10px] transition-colors ${
                  hydration >= h
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/40'
                    : 'bg-zinc-700 text-zinc-500 border border-zinc-600'
                }`}
              >
                {h}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Muscle Soreness */}
      <div className="bg-zinc-800/50 rounded-2xl p-4 border border-zinc-700/50">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-orange-400" />
          <span className="text-zinc-300 text-sm font-medium">{t('recovery.soreness')}</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {MUSCLE_GROUPS.map(muscle => (
            <button
              key={muscle.id}
              onClick={() => toggleSoreness(muscle.id)}
              className={`flex flex-col items-center gap-1 p-2.5 rounded-xl border transition-colors ${getSorenessColor(soreness[muscle.id])}`}
              data-testid={`soreness-${muscle.id}`}
            >
              <span className="text-lg">{muscle.icon}</span>
              <span className="text-white text-[10px]">{t(`recovery.muscles.${muscle.id}`)}</span>
              <span className={`text-[9px] ${soreness[muscle.id] > 0 ? 'text-orange-400' : 'text-zinc-500'}`}>
                {getSorenessLabel(soreness[muscle.id])}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full py-3 bg-yellow-500 text-black rounded-xl text-sm font-semibold disabled:opacity-50 transition-colors hover:bg-yellow-400"
        data-testid="save-recovery-btn"
      >
        {saving ? t('common.saving') : t('recovery.save_button')}
      </button>
    </div>
  );
};

export default MobileRecoveryTab;
